
import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
interface StarCounterProps {
  count: number;
  onUpdateStars?: (change: number) => void;
  editMode?: boolean;
  className?: string;
}

const StarCounter: React.FC<StarCounterProps> = ({ 
  count, 
  onUpdateStars, 
  editMode = false,
  className
}) => {
  const [animateStars, setAnimateStars] = useState(false);
  const [isFixed, setIsFixed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsFixed(scrollY > 100 && !editMode); // Only fix when not in edit mode
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [editMode]);

  const _handleAnimate = () => {
    setAnimateStars(true);
  };

  return (
    <div 
      className={cn(
        "relative inline-flex items-center", 
        animateStars && "animate-pulse-scale",
        isFixed && "fixed top-4 left-1/2 transform -translate-x-1/2 z-50 scale-90",
        className
      )}
      onAnimationEnd={() => setAnimateStars(false)}
    >
      {/* Balanced background with golden ratio proportions */}
      <div className="bg-gradient-to-br from-white/90 to-yellow-50/80 dark:from-gray-800/90 dark:to-gray-700/80 rounded-2xl border border-yellow-400/30 px-4 py-3 flex items-center space-x-3 shadow-lg">
        {editMode && onUpdateStars && (
          <button 
            onClick={() => onUpdateStars(-1)}
            className="w-8 h-8 rounded-xl bg-red-500 text-white text-lg flex items-center justify-center hover:bg-red-600 transition-colors"
            aria-label="Decrease stars"
          >
            −
          </button>
        )}
        
        {/* Balanced star and count */}
        <div className="flex items-center space-x-2">
          <Star size={20} className="text-yellow-500 fill-yellow-500" />
          <span className="font-bold text-xl text-gray-800 dark:text-white">
            {count.toLocaleString()}
          </span>
        </div>
        
        {editMode && onUpdateStars && (
          <button 
            onClick={() => onUpdateStars(1)}
            className="w-8 h-8 rounded-xl bg-green-500 text-white text-lg flex items-center justify-center hover:bg-green-600 transition-colors"
            aria-label="Increase stars"
          >
            +
          </button>
        )}
      </div>
    </div>
  );
};

export default StarCounter;
